/**
 * AuditReview — CP2–CP10 audit proposal walkthrough for Bryan.
 *
 * Each proposal is shown gate-by-gate with the locked 5-point graded scale
 * matching the calculator and Opportunity DNA helix Q&A exactly.
 *
 * UPDATED 2026-05-07: replaced 3-option (Approve / Reject / Skip) with the
 * locked 5-point graded scale + Skip.
 *
 * Scale:
 *   5 — Strong yes / confirmed / fully in place   → 4 pts  (#15803D)
 *   4 — Mostly yes / likely / mostly in place     → 3 pts  (#16A34A)
 *   3 — Partial / mixed signals / unclear         → 2 pts  (#EAB308)
 *   2 — Mostly no / weak / mostly missing         → 1 pt   (#F97316)
 *   1 — Hard no / blocked / absent                → 0 pts  (#DC2626)
 *   Skip — unknown for now                        → null (excluded)
 *
 * Score math:
 *   per_cp  = round( (sum_points_answered / (4 × count_answered)) × 100 )
 *   composite = round( (sum_all_answered / (4 × count_all_answered)) × 100 )
 *   Skip excluded from numerator AND denominator.
 *   Zero answered → render "—".
 *
 * localStorage key: "aip:cp210-audit:v3" (v3 = 5-point graded scale)
 * Export filename: cp2-10-audit-decisions-2026-05-07.json
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";

// ─── Locked 5-point graded scale (matches calculator + helix Q&A exactly) ───

interface GradedOption {
  display: number;   // 5..1
  value: string;     // "4".."0" stored as selectedOption
  points: number;    // 4..0
  anchor: string;    // Short label
  label: string;     // Full label
  borderColor: string;
}

const GRADED_OPTIONS: GradedOption[] = [
  { display: 5, value: "4", points: 4, anchor: "Strong",  label: "Strong — confirmed / fully in place",  borderColor: "#15803D" },
  { display: 4, value: "3", points: 3, anchor: "Mostly",  label: "Mostly — likely / mostly in place",    borderColor: "#16A34A" },
  { display: 3, value: "2", points: 2, anchor: "Partial", label: "Partial — mixed signals / unclear",    borderColor: "#EAB308" },
  { display: 2, value: "1", points: 1, anchor: "Weak",    label: "Weak — mostly missing / mostly no",    borderColor: "#F97316" },
  { display: 1, value: "0", points: 0, anchor: "Absent",  label: "Absent — hard no / blocked",           borderColor: "#DC2626" },
];

// ─── Score helpers ────────────────────────────────────────────────────────────

/**
 * Compute a 0–100 score from a set of graded values.
 * Returns null when no items have been answered (renders as "—").
 */
function computeGradeScore(values: Array<string | null>): number | null {
  let sum = 0;
  let count = 0;
  for (const v of values) {
    if (v === null || v === undefined) continue;
    const pts = parseInt(v, 10);
    if (!isNaN(pts)) { sum += pts; count += 1; }
  }
  if (count === 0) return null;
  return Math.round((sum / (4 * count)) * 100);
}

function scoreColor(pct: number): string {
  if (pct >= 70) return "#16A34A";
  if (pct >= 40) return "#CA8A04";
  return "#DC2626";
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ProposalAction = "REMOVE" | "EDIT" | "ADD" | "MOVE" | "MERGE";

/** Graded value stored per proposal: "4"..."0" or null (skip) */
type GradeValue = string | null;

interface Proposal {
  id: string;
  cp: number;
  action: ProposalAction;
  questionKey?: string;
  currentLabel?: string;
  proposedChange: string;
  rationale: string;
}

interface DecisionEntry {
  grade: GradeValue;  // "4"..."0" or null = skip
  notes?: string;
  decidedAt: string;
}

interface AuditState {
  decisions: Record<string, DecisionEntry>;
  startedAt: string;
}

// ─── Proposal data — embedded verbatim per specification ─────────────────────

const PROPOSALS: Proposal[] = [
  // CP2 — Need
  {
    id: "cp2-1",
    cp: 2,
    action: "REMOVE",
    questionKey: "cp2_hr_coverage",
    currentLabel: "Have you confirmed at least one HR/People Ops contact in the buying coalition?",
    proposedChange: "Remove this question entirely",
    rationale: "Manufacturing deals don't pivot on HR coverage. This is gate-padding.",
  },
  {
    id: "cp2-2",
    cp: 2,
    action: "EDIT",
    questionKey: "cp2_fox",
    currentLabel: "Fox (informal power broker) identified?",
    proposedChange: "Rename to 'Informal power broker identified?'",
    rationale: "BDRs may not know the term 'fox'. Plain English.",
  },
  {
    id: "cp2-3",
    cp: 2,
    action: "ADD",
    proposedChange:
      "Add new question: 'Buying coalition assembled — is the right room formed?' (tests whether at least Finance + Ops + Champion are engaged and aware of each other)",
    rationale:
      "We test each role separately but never roll them up into 'do we have the coalition'.",
  },

  // CP3 — Pain
  {
    id: "cp3-1",
    cp: 3,
    action: "EDIT",
    questionKey: "cp3_ci_culture",
    currentLabel: "Culture of continuous improvement?",
    proposedChange: "Reframe to 'Does the buyer have an operating cadence we can plug into?'",
    rationale: "Less jargon, same signal.",
  },
  {
    id: "cp3-2",
    cp: 3,
    action: "REMOVE",
    questionKey: "cp3_alignment",
    currentLabel: "Alignment on goals and priorities?",
    proposedChange: "Remove",
    rationale:
      "Overlaps with cp3_accountability_gap and cp3_implementation_followthrough. Three questions all probing 'are people on the same page' — pick one.",
  },
  {
    id: "cp3-3",
    cp: 3,
    action: "REMOVE",
    questionKey: "cp3_realtime_mobile",
    currentLabel: "Real-time data + mobile access need?",
    proposedChange: "Remove",
    rationale: "Feature-shopping question, not qualify/disqualify.",
  },
  {
    id: "cp3-4",
    cp: 3,
    action: "MOVE",
    questionKey: "(was removed from CP1) cp3_pain_source",
    proposedChange:
      "Bring 'Who stated the pain? (EB / champion / AE inference)' into CP3 (it was removed from CP1 because too deep for first-touch)",
    rationale: "It IS valuable, just not at CP1.",
  },
  {
    id: "cp3-5",
    cp: 3,
    action: "MOVE",
    questionKey: "(was removed from CP1) cp3_pain_corroboration",
    proposedChange: "Bring 'Pain confirmed by a second source?' into CP3",
    rationale: "Same — valuable, just not for the BDR's first screen.",
  },
  {
    id: "cp3-6",
    cp: 3,
    action: "ADD",
    proposedChange:
      "Add 'Quantified pain in dollars (deal-sizing)?' — separate from CP1's any-quantified question; this one asks the AE to size the prize so they can match scope to ROI",
    rationale: "AE needs this to size the deal.",
  },

  // CP4 — Budget
  {
    id: "cp4-1",
    cp: 4,
    action: "MERGE",
    questionKey: "cp4_training_owner",
    currentLabel: "Named training and onboarding owner?",
    proposedChange:
      "Merge into cp4_adoption_plan — adoption plan IS training plan; two questions creates artificial separation",
    rationale: "Same conversation, two slots.",
  },
  {
    id: "cp4-2",
    cp: 4,
    action: "ADD",
    proposedChange: "Add 'Does the buyer have the bench to actually execute this?' — staffing reality check",
    rationale:
      "Many deals stall not because the buyer doesn't want it but because they have no capacity to absorb it.",
  },
  {
    id: "cp4-3",
    cp: 4,
    action: "ADD",
    proposedChange:
      "Add 'Internal communication / launch plan named?' — who's telling the field, what's the message, when",
    rationale: "Adoption fails on Day 1 without internal comms.",
  },

  // CP5 — Timeline
  {
    id: "cp5-1",
    cp: 5,
    action: "EDIT",
    questionKey: "cp5_data_quality",
    currentLabel: "Data environment quality?",
    proposedChange:
      "Reframe to 'Will the buyer's data work for our system on Day 1, or do we need a data prep project first?'",
    rationale: "Decision-relevant version of the same question.",
  },
  {
    id: "cp5-2",
    cp: 5,
    action: "ADD",
    proposedChange: "Add 'Identity / SSO requirements known?' — basic infosec gate",
    rationale:
      "Currently buried inside security review but it's a different question and often a separate workstream.",
  },

  // CP7 — Authority
  {
    id: "cp7-1",
    cp: 7,
    action: "REMOVE",
    questionKey: "cp7_capex_opex",
    currentLabel: "CapEx vs. OpEx classification?",
    proposedChange: "Remove from CP7 (move to a 'deal mechanics' form later)",
    rationale:
      "Operationally interesting, not qualify/disqualify. CapEx vs OpEx affects close mechanics, not whether we should keep working the deal.",
  },
  {
    id: "cp7-2",
    cp: 7,
    action: "MOVE",
    questionKey: "cp7_legal_msa",
    currentLabel: "Legal / MSA status?",
    proposedChange: "Move to CP10 (this is a paper/legal question, not budget)",
    rationale: "CP10 is where written commitment and contract status belong.",
  },
  {
    id: "cp7-3",
    cp: 7,
    action: "ADD",
    proposedChange: "Add 'Has anyone bought from us before at this price point?' — sanity check for AE",
    rationale:
      "If we're 3x our highest deal at this account, the price is going to need a justification story we may not have.",
  },

  // CP8 — Competition
  {
    id: "cp8-1",
    cp: 8,
    action: "REMOVE",
    questionKey: "cp8_next_steps",
    currentLabel: "Next steps booked?",
    proposedChange: "Remove",
    rationale:
      "Overlaps with cp8_map (mutual action plan). If we have a signed MAP we have next steps. Redundant.",
  },
  {
    id: "cp8-2",
    cp: 8,
    action: "ADD",
    proposedChange:
      "Add 'Are we tracking ahead of, on, or behind the buyer's stated timeline?' — pace check",
    rationale:
      "Lets the AE see when a deal is slipping in real time, not just at end-of-quarter.",
  },

  // CP9 — Finance
  {
    id: "cp9-1",
    cp: 9,
    action: "ADD",
    proposedChange:
      "Add 'Have we shown why our solution is meaningfully different (not just better) for them specifically?'",
    rationale: "Many deals are lost not on price but on undifferentiation.",
  },
  {
    id: "cp9-2",
    cp: 9,
    action: "ADD",
    proposedChange: "Add 'Did the buyer evaluate us against an internal-build option?'",
    rationale:
      "For manufacturing buyers especially, 'we'll just build it ourselves' is a real competitor.",
  },

  // CP10 — Legal — Contracts, SLAs, Terms (Legal)
  {
    id: "cp10-1",
    cp: 10,
    action: "MOVE",
    proposedChange:
      "Pull cp7_legal_msa (Legal/MSA status) into CP10 from CP7 (this is the same MOVE as cp7-2 — confirms the destination)",
    rationale: "Paper questions live at the legal gate.",
  },
  {
    id: "cp10-2",
    cp: 10,
    action: "ADD",
    proposedChange: "Add 'Counter-signed by both sides?' — a one-sided signed commitment isn't a deal",
    rationale: "Many deals die at the buyer's countersign step.",
  },
  {
    id: "cp10-3",
    cp: 10,
    action: "ADD",
    proposedChange: "Add 'Implementation kickoff date locked?' — once paper is signed, the deal is real only if we know when we start",
    rationale: "Without a kickoff date, paper commitments still slip.",
  },
];

// CP6 special info card id
const CP6_INFO_ID = "cp6-1";

// Canonical CP gate names — locked 2026-05-26 per helix-checkpoints.ts
const GATE_NAMES: Record<number, string> = {
  1:  "Project",
  2:  "Need / Pain / Demonstration",
  3:  "Budget",
  4:  "Timeline",
  5:  "Authority — Project / Initiative",
  6:  "Technical and Financial Sign-off",
  7:  "Org Structure and Influence Map",
  8:  "Competitive Position",
  9:  "Compliance — IT Internal and Industry-Specific",
  10: "Legal — Contracts, SLAs, Terms",
};

// Action badge styles
const ACTION_BADGE: Record<ProposalAction, { bg: string; color: string }> = {
  REMOVE: { bg: "#FEE2E2", color: "#991B1B" },
  EDIT:   { bg: "#FEF3C7", color: "#92400E" },
  ADD:    { bg: "#D1FAE5", color: "#065F46" },
  MOVE:   { bg: "#EFEFEE", color: "#0B0B0F" },
  MERGE:  { bg: "#FEE2E2", color: "#0B0B0F" },
};

// ─── Storage helpers (localStorage cache + server) ───────────────────────────

const STORAGE_KEY = "aip:cp210-audit:v3";
const AUDIT_VERSION = "v3";

function emptyState(): AuditState {
  return { decisions: {}, startedAt: new Date().toISOString() };
}

function loadState(): AuditState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as AuditState;
  } catch {}
  return emptyState();
}

function saveState(s: AuditState) {
  // 1. localStorage cache (immediate)
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
  // 2. Server write (fire-and-forget — localStorage is offline fallback)
  fetch("/api/audit-review", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      version:    AUDIT_VERSION,
      decisions:  s.decisions,
      user_added: {},
      started_at: s.startedAt,
    }),
  }).catch(() => {
    // Server write failed — data safe in localStorage cache.
  });
}

/** Hydrate state from server on mount, falling back to localStorage cache. */
async function loadStateFromServer(): Promise<AuditState | null> {
  try {
    const res = await fetch(`/api/audit-review?version=${AUDIT_VERSION}`, { credentials: "include" });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.session) return null;
    return {
      decisions:  json.session.decisions  ?? {},
      startedAt:  json.session.started_at ?? new Date().toISOString(),
    } as AuditState;
  } catch {
    return null;
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AuditReview() {
  const [state, setState] = useState<AuditState>(() => loadState());

  // On mount: hydrate from server (overwrites localStorage cache with server truth).
  // This ensures cross-device restore: state from server > state from localStorage.
  useEffect(() => {
    loadStateFromServer().then((serverState) => {
      if (!serverState) return; // not logged in or no session yet — keep localStorage state
      // Use server state if it has more decisions than local cache
      // (server is source of truth; local may be stale or empty on new device)
      setState((local) => {
        const serverCount = Object.keys(serverState.decisions).length;
        const localCount  = Object.keys(local.decisions).length;
        // Prefer whichever has more graded items (handles new device AND mid-session merge)
        if (serverCount >= localCount) return serverState;
        return local;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { saveState(state); }, [state]);

  const gateRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // ── Decision helpers ────────────────────────────────────────────────────────

  function setGrade(id: string, grade: GradeValue) {
    setState((s) => ({
      ...s,
      decisions: {
        ...s.decisions,
        [id]: { grade, decidedAt: new Date().toISOString() },
      },
    }));
  }

  function clearDecision(id: string) {
    setState((s) => {
      const next = { ...s.decisions };
      delete next[id];
      return { ...s, decisions: next };
    });
  }

  function resetAll() {
    if (!window.confirm("Reset all grades? This cannot be undone.")) return;
    setState(emptyState());
  }

  // ── Progress ────────────────────────────────────────────────────────────────

  const totalItems = PROPOSALS.length + 1; // +1 for cp6 info card

  const reviewedCount = useMemo(
    () => Object.keys(state.decisions).length,
    [state.decisions]
  );

  const pct = totalItems ? Math.round((reviewedCount / totalItems) * 100) : 0;

  // ── Composite score (5-point math across all graded items, skip excluded) ───

  const compositeScore = useMemo(() => {
    const grades = Object.values(state.decisions).map((d) => d.grade);
    return computeGradeScore(grades);
  }, [state.decisions]);

  // ── Per-gate ────────────────────────────────────────────────────────────────

  const proposalsByGate = useMemo(() => {
    const map: Record<number, Proposal[]> = {};
    PROPOSALS.forEach((p) => {
      if (!map[p.cp]) map[p.cp] = [];
      map[p.cp].push(p);
    });
    return map;
  }, []);

  const GATES = [2, 3, 4, 5, 6, 7, 8, 9, 10];

  function gateGrades(cp: number): GradeValue[] {
    if (cp === 6) {
      const d = state.decisions[CP6_INFO_ID];
      return d ? [d.grade] : [];
    }
    return (proposalsByGate[cp] ?? []).map((p) => state.decisions[p.id]?.grade ?? null);
  }

  function gateGradedCount(cp: number): number {
    if (cp === 6) return state.decisions[CP6_INFO_ID] ? 1 : 0;
    return (proposalsByGate[cp] ?? []).filter((p) => state.decisions[p.id]).length;
  }

  function gateTotalCount(cp: number): number {
    if (cp === 6) return 1;
    return (proposalsByGate[cp] ?? []).length;
  }

  function gateScore(cp: number): number | null {
    return computeGradeScore(gateGrades(cp));
  }

  // ── Download ────────────────────────────────────────────────────────────────

  function downloadDecisions() {
    const payload = {
      exportedAt: new Date().toISOString(),
      startedAt: state.startedAt,
      scaleVersion: "5-point-graded-v1",
      summary: {
        total_items: totalItems,
        graded: reviewedCount,
        composite_score: compositeScore !== null ? `${compositeScore}%` : "—",
      },
      decisions: state.decisions,
      proposals: PROPOSALS.map((p) => ({
        ...p,
        grade: state.decisions[p.id]?.grade ?? null,
        score_points:
          state.decisions[p.id]?.grade !== null && state.decisions[p.id]?.grade !== undefined
            ? parseInt(state.decisions[p.id].grade as string, 10)
            : null,
      })),
      cp6_confirmation: state.decisions[CP6_INFO_ID] ?? null,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cp2-10-audit-decisions-2026-05-07.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function scrollToGate(cp: number) {
    const el = gateRefs.current[cp];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF7", padding: "24px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}>
          <div>
            <div style={{
              fontSize: 11,
              color: "#6B6B70",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 4,
            }}>
              Manager — CP2 to CP10 Audit Review
            </div>
            <h1 style={{
              fontSize: 28,
              fontWeight: 700,
              color: "#0B0B0F",
              margin: "0 0 6px 0",
              lineHeight: 1.2,
            }}>
              Walk my proposed changes, checkpoint by checkpoint
            </h1>
            <p style={{
              fontSize: 14,
              color: "#6B6B70",
              margin: 0,
              maxWidth: 680,
              lineHeight: 1.6,
            }}>
              Rate each proposal on a 5-point scale. Strong to Absent.
              Grades are aggregated into a composite score per checkpoint and overall.
              Skip excludes the proposal from the score.
            </p>
          </div>
          <Link href="/pilot">
            <a style={{
              fontSize: 13,
              color: "#374151",
              textDecoration: "none",
              padding: "8px 12px",
              border: "1px solid #D1D5DB",
              borderRadius: 6,
              background: "#fff",
              whiteSpace: "nowrap",
            }}>
              Back to home
            </a>
          </Link>
        </div>

        {/* Progress + composite score bar */}
        <div style={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6, gap: 8, flexWrap: "wrap" }}>
              <span style={{ color: "#374151" }}>
                Graded: <strong>{reviewedCount}</strong> of {totalItems}
                <span style={{ color: "#9CA3AF", fontWeight: 400 }}> ({pct}%)</span>
              </span>
              {compositeScore !== null ? (
                <span style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: scoreColor(compositeScore),
                }}>
                  Composite: {compositeScore}%
                </span>
              ) : (
                <span style={{ fontSize: 13, color: "#9CA3AF" }}>Composite: —</span>
              )}
            </div>
            <div style={{ height: 6, background: "#E5E7EB", borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${pct}%`,
                background: compositeScore !== null ? scoreColor(compositeScore) : "#16A34A",
                transition: "width 0.3s",
              }} />
            </div>
          </div>
          <button
            onClick={resetAll}
            style={{
              fontSize: 12,
              padding: "6px 10px",
              background: "#fff",
              color: "#991B1B",
              border: "1px solid #FCA5A5",
              borderRadius: 6,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Reset all
          </button>
        </div>

        {/* Gate tab strip */}
        <div style={{ display: "flex", gap: 6, marginBottom: 24, flexWrap: "wrap" }}>
          {GATES.map((cp) => {
            const graded = gateGradedCount(cp);
            const total = gateTotalCount(cp);
            const allDone = total > 0 && graded === total;
            const gs = gateScore(cp);
            return (
              <button
                key={cp}
                onClick={() => scrollToGate(cp)}
                style={{
                  padding: "8px 12px",
                  background: "#fff",
                  color: allDone ? "#065F46" : "#374151",
                  border: `1px solid ${allDone ? "#86EFAC" : "#D1D5DB"}`,
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 100,
                  transition: "border-color 0.2s",
                }}
              >
                <span style={{ fontSize: 10, color: "#9CA3AF", marginBottom: 1 }}>
                  CP{cp}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                  {GATE_NAMES[cp]}
                </span>
                <span style={{
                  fontSize: 10,
                  color: gs !== null ? scoreColor(gs) : allDone ? "#065F46" : "#6B7280",
                  fontWeight: gs !== null ? 700 : 400,
                }}>
                  {gs !== null ? `${gs}%` : allDone ? "—" : `${graded}/${total}`}
                </span>
              </button>
            );
          })}
        </div>

        {/* Gate sections */}
        {GATES.map((cp) => {
          const gs = gateScore(cp);
          return (
            <div
              key={cp}
              ref={(el) => { gateRefs.current[cp] = el; }}
              style={{ marginBottom: 40, scrollMarginTop: 24 }}
            >
              {/* Gate header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 14,
                flexWrap: "wrap",
              }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#374151",
                  background: "#F3F4F6",
                  border: "1px solid #E5E7EB",
                  borderRadius: 6,
                  padding: "4px 10px",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}>
                  CP{cp}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#0B0B0F" }}>
                  {GATE_NAMES[cp]}
                </div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 4 }}>
                  {gateGradedCount(cp)}/{gateTotalCount(cp)} graded
                </div>
                {/* Gate score pill */}
                <div style={{
                  marginLeft: "auto",
                  fontSize: 13,
                  fontWeight: 700,
                  color: gs !== null ? scoreColor(gs) : "#8E8E94",
                  background: gs !== null ? `${scoreColor(gs)}14` : "#EFEFEE",
                  border: `1px solid ${gs !== null ? scoreColor(gs) + "33" : "#E5E5E7"}`,
                  borderRadius: 6,
                  padding: "3px 10px",
                }}>
                  {gs !== null ? `${gs}%` : "—"}
                </div>
              </div>

              {/* CP6 special info card */}
              {cp === 6 ? (
                <CP6InfoCard
                  grade={state.decisions[CP6_INFO_ID]?.grade ?? null}
                  onGrade={(g) => setGrade(CP6_INFO_ID, g)}
                  onClear={() => clearDecision(CP6_INFO_ID)}
                />
              ) : (
                (proposalsByGate[cp] ?? []).map((proposal) => {
                  const entry = state.decisions[proposal.id];
                  const grade = entry?.grade ?? null;

                  return (
                    <ProposalCard
                      key={proposal.id}
                      proposal={proposal}
                      grade={grade}
                      onGrade={(g) => setGrade(proposal.id, g)}
                      onClear={() => clearDecision(proposal.id)}
                    />
                  );
                })
              )}
            </div>
          );
        })}

        {/* Download */}
        <div style={{
          borderTop: "1px solid #E5E7EB",
          paddingTop: 24,
          marginTop: 8,
          display: "flex",
          justifyContent: "center",
        }}>
          <button
            onClick={downloadDecisions}
            style={{
              padding: "12px 28px",
              background: "#0B0B0F",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              letterSpacing: "0.01em",
            }}
          >
            Download my grades
          </button>
        </div>
        <div style={{
          textAlign: "center",
          fontSize: 12,
          color: "#9CA3AF",
          marginTop: 8,
          marginBottom: 32,
        }}>
          Saves as cp2-10-audit-decisions-2026-05-07.json
        </div>
      </div>
    </div>
  );
}

// ─── ProposalCard ─────────────────────────────────────────────────────────────

interface ProposalCardProps {
  proposal: Proposal;
  grade: GradeValue;
  onGrade: (g: GradeValue) => void;
  onClear: () => void;
}

function ProposalCard({ proposal, grade, onGrade, onClear }: ProposalCardProps) {
  const badge = ACTION_BADGE[proposal.action];
  const hasGrade = grade !== null && grade !== undefined;
  const selOpt = hasGrade ? GRADED_OPTIONS.find((o) => o.value === grade) : null;

  // Left border color from the selected option
  const leftBorderColor = selOpt ? selOpt.borderColor : "#E5E7EB";

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderLeft: `4px solid ${leftBorderColor}`,
        borderRadius: 8,
        padding: "18px 20px",
        marginBottom: 12,
        transition: "border-left-color 0.25s",
      }}
    >
      {/* Eyebrow */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{
          fontSize: 11,
          color: "#9CA3AF",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
        }}>
          CP{proposal.cp} — {GATE_NAMES[proposal.cp]}
        </span>
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          padding: "2px 7px",
          borderRadius: 4,
          background: badge.bg,
          color: badge.color,
          textTransform: "uppercase",
        }}>
          {proposal.action}
        </span>
        {selOpt && (
          <span style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 4,
            background: `${selOpt.borderColor}18`,
            color: selOpt.borderColor,
            border: `1px solid ${selOpt.borderColor}33`,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}>
            {selOpt.display} — {selOpt.anchor}
          </span>
        )}
      </div>

      {/* Current question */}
      {proposal.currentLabel && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>
            Current question
          </div>
          <div style={{ fontSize: 14, color: "#374151" }}>{proposal.currentLabel}</div>
          {proposal.questionKey && (
            <div style={{ fontSize: 11, fontFamily: "monospace", color: "#9CA3AF", marginTop: 3 }}>
              {proposal.questionKey}
            </div>
          )}
        </div>
      )}
      {!proposal.currentLabel && proposal.questionKey && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontFamily: "monospace", color: "#9CA3AF" }}>
            {proposal.questionKey}
          </div>
        </div>
      )}

      {/* Proposed change */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>
          Proposed change
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#0B0B0F", lineHeight: 1.5 }}>
          {proposal.proposedChange}
        </div>
      </div>

      {/* Rationale */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>
          Why
        </div>
        <div style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.6 }}>
          {proposal.rationale}
        </div>
      </div>

      {/* 5-point graded scale — stacked rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 10 }}>
        {GRADED_OPTIONS.map((opt) => {
          const isSel = grade === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onGrade(isSel ? null : opt.value)}
              style={{
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "11px 14px 11px 16px",
                borderRadius: 8,
                border: isSel ? `2px solid ${opt.borderColor}` : "1px solid #E5E5E7",
                borderLeft: `4px solid ${opt.borderColor}`,
                background: isSel ? `${opt.borderColor}10` : "#FFFFFF",
                color: "#0B0B0F",
                fontSize: 15,
                lineHeight: 1.45,
                cursor: "pointer",
                transition: "background 0.1s, border-color 0.1s",
                width: "100%",
              }}
              onMouseEnter={(e) => {
                if (!isSel) (e.currentTarget as HTMLButtonElement).style.background = "#FAFAF7";
              }}
              onMouseLeave={(e) => {
                if (!isSel) (e.currentTarget as HTMLButtonElement).style.background = "#FFFFFF";
              }}
            >
              <span style={{ fontWeight: isSel ? 700 : 500 }}>
                <span style={{ color: "#6B6B70", fontSize: 13, marginRight: 8 }}>{opt.display}</span>
                {opt.label}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#6B6B70", whiteSpace: "nowrap", flexShrink: 0 }}>
                {opt.points} {opt.points === 1 ? "pt" : "pts"}
              </span>
            </button>
          );
        })}
      </div>

      {/* Skip link */}
      <div style={{ textAlign: "center" }}>
        <button
          type="button"
          onClick={() => onClear()}
          style={{
            background: "none",
            border: "none",
            color: "#8E8E94",
            fontSize: 12,
            cursor: "pointer",
            textDecoration: "underline",
            padding: "4px 8px",
          }}
        >
          Skip — unknown for now
        </button>
      </div>
    </div>
  );
}

// ─── CP6 info card ────────────────────────────────────────────────────────────

interface CP6InfoCardProps {
  grade: GradeValue;
  onGrade: (g: GradeValue) => void;
  onClear: () => void;
}

function CP6InfoCard({ grade, onGrade, onClear }: CP6InfoCardProps) {
  const selOpt = grade !== null ? GRADED_OPTIONS.find((o) => o.value === grade) : null;
  const leftBorderColor = selOpt ? selOpt.borderColor : "#E5E7EB";

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderLeft: `4px solid ${leftBorderColor}`,
        borderRadius: 8,
        padding: "20px 22px",
        marginBottom: 12,
        transition: "border-left-color 0.25s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.12em" }}>
          CP6 — Authority
        </span>
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          padding: "2px 7px",
          borderRadius: 4,
          background: "#F3F4F6",
          color: "#374151",
          textTransform: "uppercase",
        }}>
          No changes
        </span>
        {selOpt && (
          <span style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 4,
            background: `${selOpt.borderColor}18`,
            color: selOpt.borderColor,
            border: `1px solid ${selOpt.borderColor}33`,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}>
            {selOpt.display} — {selOpt.anchor}
          </span>
        )}
      </div>

      <div style={{ fontSize: 15, fontWeight: 600, color: "#0B0B0F", marginBottom: 8 }}>
        CP6 — Authority
      </div>
      <div style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.6, marginBottom: 18 }}>
        Proposing zero changes to this checkpoint. The 12 questions deployed on May 6 are tight.
        Rate your agreement with leaving CP6 as-is.
      </div>

      {/* 5-point graded scale */}
      <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 10 }}>
        {GRADED_OPTIONS.map((opt) => {
          const isSel = grade === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onGrade(isSel ? null : opt.value)}
              style={{
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "11px 14px 11px 16px",
                borderRadius: 8,
                border: isSel ? `2px solid ${opt.borderColor}` : "1px solid #E5E5E7",
                borderLeft: `4px solid ${opt.borderColor}`,
                background: isSel ? `${opt.borderColor}10` : "#FFFFFF",
                color: "#0B0B0F",
                fontSize: 15,
                lineHeight: 1.45,
                cursor: "pointer",
                transition: "background 0.1s, border-color 0.1s",
                width: "100%",
              }}
              onMouseEnter={(e) => {
                if (!isSel) (e.currentTarget as HTMLButtonElement).style.background = "#FAFAF7";
              }}
              onMouseLeave={(e) => {
                if (!isSel) (e.currentTarget as HTMLButtonElement).style.background = "#FFFFFF";
              }}
            >
              <span style={{ fontWeight: isSel ? 700 : 500 }}>
                <span style={{ color: "#6B6B70", fontSize: 13, marginRight: 8 }}>{opt.display}</span>
                {opt.label}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#6B6B70", whiteSpace: "nowrap", flexShrink: 0 }}>
                {opt.points} {opt.points === 1 ? "pt" : "pts"}
              </span>
            </button>
          );
        })}
      </div>

      {/* Skip link */}
      <div style={{ textAlign: "center" }}>
        <button
          type="button"
          onClick={onClear}
          style={{
            background: "none",
            border: "none",
            color: "#8E8E94",
            fontSize: 12,
            cursor: "pointer",
            textDecoration: "underline",
            padding: "4px 8px",
          }}
        >
          Skip — unknown for now
        </button>
      </div>
    </div>
  );
}
